import React, { createContext, useContext, useEffect, useMemo } from "react";
import { domainThemeOverrides } from "./domainThemes";
import { defaultTheme, resolveTheme } from "./theme";
import { AppTheme, DomainThemeOverrides } from "./types";

const ThemeContext = createContext<AppTheme>(defaultTheme);

interface ThemeProviderProps {
    children: React.ReactNode;
    hostname?: string;
    overrides?: DomainThemeOverrides;
}

const setLinkHref = (selector: string, rel: string, href: string): void => {
    let link = document.head.querySelector<HTMLLinkElement>(selector);
    if (!link) {
        link = document.createElement("link");
        link.rel = rel;
        document.head.appendChild(link);
    }
    link.href = href;
};

const createManifestUrl = (theme: AppTheme): string => {
    const createIcon = (src: string, size: string) => {
        const svg = src.toLowerCase().split(/[?#]/)[0].endsWith(".svg");
        return {
            src,
            type: svg ? "image/svg+xml" : "image/png",
            sizes: svg ? "any" : size,
            purpose: "any maskable",
        };
    };
    const manifest = {
        short_name: theme.brand.shortName,
        name: theme.brand.companyName,
        id: "/",
        start_url: "/",
        scope: "/",
        display: "standalone",
        theme_color: theme.colors.browserTheme,
        background_color: theme.colors.browserTheme,
        icons: [
            createIcon(theme.assets.pwaIcon192, "192x192"),
            createIcon(theme.assets.pwaIcon512, "512x512"),
        ],
    };

    return `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
    children,
    hostname,
    overrides = domainThemeOverrides,
}) => {
    const theme = useMemo(
        () => resolveTheme(
            hostname ?? (typeof window === "undefined" ? defaultTheme.domain.root : window.location.hostname),
            overrides,
        ),
        [hostname, overrides],
    );

    useEffect(() => {
        const root = document.documentElement;
        const variables: Record<string, string> = {
            "--theme-primary": theme.colors.primary,
            "--theme-primary-hover": theme.colors.primaryHover,
            "--theme-primary-active": theme.colors.primaryActive,
            "--theme-link-hover": theme.colors.linkHover,
            "--theme-focus-border": theme.colors.focusBorder,
            "--theme-primary-contrast": theme.colors.primaryContrast,
            "--theme-sidebar-background": theme.colors.sidebarBackground,
            "--theme-topbar-background": theme.colors.topbarBackground,
            "--theme-page-background": theme.colors.pageBackground,
            "--theme-portal-primary": theme.colors.portalPrimary,
            "--theme-portal-primary-strong": theme.colors.portalPrimaryStrong,
        };

        Object.entries(variables).forEach(([name, value]) => root.style.setProperty(name, value));
        root.dataset.themeRootDomain = theme.domain.root;

        setLinkHref("link[rel~='icon']", "icon", theme.assets.favicon);
        setLinkHref("link[rel='apple-touch-icon']", "apple-touch-icon", theme.assets.appleTouchIcon);
        setLinkHref("link[rel='manifest']", "manifest", createManifestUrl(theme));

        const themeMeta = document.head.querySelector<HTMLMetaElement>("meta[name='theme-color']");
        themeMeta?.setAttribute("content", theme.colors.browserTheme);

        if (!document.title || document.title === defaultTheme.brand.companyName) {
            document.title = theme.brand.companyName;
        }
    }, [theme]);

    return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): AppTheme => useContext(ThemeContext);
